import type { Namespace } from './types';
export const zh: Namespace = {
  proxies: {
    title: '代理',
    empty: '没有符合条件的代理',
    access: {
      public: '公开',
      private: '私有',
      unknown: '未知可见性',
    },
    kpi: {
      access: '访问',
      active: '启用',
    },
    health: {
      active: '启用',
      inactive: '停用',
    },
    badges: {
      publicReadonly: '公开只读',
      broken: '熔断',
      fails: '连败 {{count}}',
    },
    fields: {
      name: '名称',
      protocol: '协议',
      access: '访问',
      outlet: '出口/IP',
      latency: '延迟(ms)',
      active: '启用',
      createdAt: '创建时间',
      updatedAt: '更新时间',
    },
    search: {
      name: '名称',
    },
    actions: {
      create: '新增代理',
      createTitle: '新增代理',
      created: '已创建',
      edit: '编辑',
      editTitle: '编辑代理',
      checkSpeed: '测速',
      checkSpeedConfirm: '测速 {{count}} 个代理？',
      checkSpeedDispatched: '已派发 {{count}} 个测速任务，结果稍后刷新。',
      checkSpeedAll: '一键测速',
      checkSpeedAllConfirm: '对全部可见代理发起测速？',
      checkSpeedAllCapped: '可见 {{total}} 条，本次派发 {{dispatched}} 条（单次有上限）。',
      batchCheckSpeed: '批量测速',
      delete: '删除',
      deleteConfirm: '删除 {{count}} 个代理？',
      batchDelete: '批量删除',
      batchDeleteBlocked: '只能删除自己创建的代理；别人的公开代理不可删',
      deleted: '已删除',
      done: '操作已提交',
    },
    modal: {
      name: '名称',
      access: '访问',
      concurrent: '并发上限 (1-100)',
      keepIfEmpty: '留空表示不修改',
      providerDns: '供应商 DNS',
      dnsPrimary: '主',
      dnsBackup: '备{{index}}',
      dnsAdd: '添加备用端点',
      dnsRemove: '删除该端点',
      dnsEmptyHint: '未配置 —— 解析走公共兜底（选填，最多 3 个，顺序为主备）',
      dnsTooMany: '最多 3 条（主 + 两个备）',
      dnsNotHttps: '必须以 https:// 开头',
      dnsReservedIp: 'IP 字面量不得是内网 / 保留段',
      dnsEmptyEntry: '空行不是有效端点',
      dnsDuplicate: '与前面的端点重复',
      dnsNotClearable: '不支持清空 —— 想恢复公共解析请删除代理后重建',
    },
    import: {
      tab: {
        subscription: '订阅链接',
        paste: '链接录入',
        file: '文件导入',
      },
      subscriptionPlaceholder: '订阅站给的链接（https://…）',
      fetch: '拉取',
      pastePlaceholder: 'vless://…\nvmess://…\n一行一条',
      fileDrop: '把导出的文本文件拖到这里，或点击选择文件',
      submit: '提交 {{count}} 行',
      rowCount: '可导入 {{count}} 行',
      failedRows: '另有 {{count}} 行解析失败',
      column: {
        failed: '解析失败',
        uri: 'URI',
        remove: '移除',
      },
      emptyContent: '文件内容为空',
      notTextFile: '不是文本文件',
      parseFailed: '{{total}} 行里没有一行能认出协议',
      unknownScheme: '认不出协议',
      unsupportedScheme: '不支持的协议：{{detail}}',
      invalidUrl: '链接拆不出地址和端口',
      invalidHost: '地址不合法：{{detail}}',
      invalidPort: '端口不合法：{{detail}}',
      noProxiesFound: '去掉空行和注释后一行不剩',
      unrecognizedFormat: '这段文本既不是代理链接，也不是能解开的订阅内容',
      clashYamlInvalid: 'Clash 配置解析失败：{{detail}}',
    },
  },
};

export const en: Namespace = {
  proxies: {
    title: 'Proxies',
    empty: 'No proxies match the filters',
    access: {
      public: 'Public',
      private: 'Private',
      unknown: 'Unknown visibility',
    },
    kpi: {
      access: 'Access',
      active: 'Active',
    },
    health: {
      active: 'Active',
      inactive: 'Inactive',
    },
    badges: {
      publicReadonly: 'Public (read-only)',
      broken: 'Circuit open',
      fails: '{{count}} fail streak',
    },
    fields: {
      name: 'Name',
      protocol: 'Protocol',
      access: 'Access',
      outlet: 'Egress/IP',
      latency: 'Latency (ms)',
      active: 'Active',
      createdAt: 'Created at',
      updatedAt: 'Updated at',
    },
    search: {
      name: 'Name',
    },
    actions: {
      create: 'Add proxy',
      createTitle: 'Add proxies',
      created: 'Created',
      edit: 'Edit',
      editTitle: 'Edit proxy',
      checkSpeed: 'Check speed',
      checkSpeedConfirm: 'Check speed of {{count}} proxy(ies)?',
      checkSpeedDispatched: 'Dispatched {{count}} speed check(s); results arrive shortly.',
      checkSpeedAll: 'Check all',
      checkSpeedAllConfirm: 'Run a speed check on every visible proxy?',
      checkSpeedAllCapped: '{{total}} visible, {{dispatched}} dispatched — one pass is capped.',
      batchCheckSpeed: 'Check selected',
      delete: 'Delete',
      deleteConfirm: 'Delete {{count}} proxy(ies)?',
      batchDelete: 'Delete selected',
      batchDeleteBlocked:
        'Only proxies you created can be deleted; public proxies from others cannot',
      deleted: 'Deleted',
      done: 'Submitted',
    },
    modal: {
      name: 'Name',
      access: 'Access',
      concurrent: 'Max concurrent (1-100)',
      keepIfEmpty: 'Leave empty to keep unchanged',
      providerDns: 'Provider DNS',
      dnsPrimary: 'Primary',
      dnsBackup: 'Backup {{index}}',
      dnsAdd: 'Add backup endpoint',
      dnsRemove: 'Remove this endpoint',
      dnsEmptyHint:
        'Not configured — public DNS fallback (optional, up to 3, order = primary/backup)',
      dnsTooMany: 'At most 3 entries (primary + 2 fallbacks)',
      dnsNotHttps: 'Must start with https://',
      dnsReservedIp: 'IP literal must not be private / reserved',
      dnsEmptyEntry: 'An empty row is not a valid endpoint',
      dnsDuplicate: 'Duplicates an earlier endpoint',
      dnsNotClearable: 'Cannot be cleared — delete and recreate the proxy to restore public DNS',
    },
    import: {
      tab: {
        subscription: 'Subscription URL',
        paste: 'Paste links',
        file: 'Import file',
      },
      subscriptionPlaceholder: 'Subscription URL (https://…)',
      fetch: 'Fetch',
      pastePlaceholder: 'vless://…\nvmess://…\none per line',
      fileDrop: 'Drop the exported text file here, or click to choose a file',
      submit: 'Submit {{count}}',
      rowCount: '{{count}} importable',
      failedRows: '{{count}} more line(s) failed to parse',
      column: {
        failed: 'Failed',
        uri: 'URI',
        remove: 'Remove',
      },
      emptyContent: 'The file is empty',
      notTextFile: 'The file is not a text file',
      parseFailed: 'None of the {{total}} line(s) had a recognizable scheme',
      unknownScheme: 'Unknown scheme',
      unsupportedScheme: 'Unsupported scheme: {{detail}}',
      invalidUrl: 'No host/port could be read from the link',
      invalidHost: 'Invalid host: {{detail}}',
      invalidPort: 'Invalid port: {{detail}}',
      noProxiesFound: 'Nothing left after blank lines and comments',
      unrecognizedFormat: 'Not a proxy link, and not a decodable subscription either',
      clashYamlInvalid: 'Failed to parse the Clash config: {{detail}}',
    },
  },
};
